import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Q_TYPE_AR } from "@/v2/lib/rbac";
import { fileUrl, uploadFile } from "@/v2/lib/files";
import { GlassCard, Chip, Skeleton, Modal, Confirm, Bar, Toggle, PageHead } from "@/v2/admin/ui";
import { FileQuestion, Plus, Pencil, Trash2, Copy, ArrowUp, ArrowDown, Eye, Save, UploadCloud, MousePointerClick, Code2 } from "lucide-react";

function QuestionForm({ cid, q, onClose }) {
  const qc = useQueryClient();
  const isNew = !q?.id;
  const [f, setF] = useState({
    type: q?.type || "mcq", text: q?.text || "",
    options: q?.options?.length ? [...q.options] : ["", ""],
    correct: q?.correct ?? (q?.type === "multi" || q?.type === "ordering" ? [] : "0"),
    weight: q?.weight || 1, explanation: q?.explanation || "",
    model_answer: q?.model_answer || "", rubric: q?.rubric || "",
    required_keywords: (q?.required_keywords || []).join("، "),
    min_score: q?.min_score ?? 50,
    config: {
      lefts: q?.config?.lefts?.length ? [...q.config.lefts] : ["", ""],
      rights: q?.config?.rights?.length ? [...q.config.rights] : ["", ""],
      image_url: q?.config?.image_url || "",
      image_alt: q?.config?.image_alt || "صورة تفاعلية للسؤال",
      starter: q?.config?.starter || "",
    },
    hotspot: q?.type === "hotspot" && q?.correct ? { ...q.correct } : { x: 50, y: 50, w: 18, h: 18 },
    formula_answers: q?.type === "formula_fix"
      ? (Array.isArray(q?.correct) ? q.correct.join("\n") : (q?.correct || "")) : "",
  });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const isChoice = ["mcq", "multi", "truefalse", "ordering"].includes(f.type);
  const isText = ["text", "task", "scenario"].includes(f.type);
  const isMatching = f.type === "matching";
  const isHotspot = f.type === "hotspot";
  const isFormula = f.type === "formula_fix";

  const changeType = (t) => {
    let opts = f.options, correct = f.correct;
    if (t === "truefalse") { opts = ["صحيح", "خطأ"]; correct = "0"; }
    else if (t === "multi") correct = Array.isArray(correct) ? correct : [];
    else if (t === "ordering") correct = opts.map((_, i) => String(i));
    else if (t === "mcq") correct = "0";
    else if (t === "matching") correct = (f.config.lefts || []).map((_, i) => String(i));
    else if (t === "hotspot") correct = f.hotspot;
    else if (t === "formula_fix") correct = [];
    setF({ ...f, type: t, options: opts, correct });
  };
  const setOpt = (i, v) => { const o = [...f.options]; o[i] = v; setF({ ...f, options: o }); };
  const addOpt = () => setF({ ...f, options: [...f.options, ""] });
  const delOpt = (i) => {
    const o = f.options.filter((_, x) => x !== i);
    setF({ ...f, options: o, correct: f.type === "ordering" ? o.map((_, x) => String(x)) : f.type === "multi" ? [] : "0" });
  };
  const setPair = (side, i, value) => {
    const values = [...f.config[side]];
    values[i] = value;
    setF({ ...f, config: { ...f.config, [side]: values } });
  };
  const addPair = () => setF({ ...f, config: {
    ...f.config, lefts: [...f.config.lefts, ""], rights: [...f.config.rights, ""],
  } });
  const delPair = (i) => setF({ ...f, config: {
    ...f.config,
    lefts: f.config.lefts.filter((_, x) => x !== i),
    rights: f.config.rights.filter((_, x) => x !== i),
  } });
  const chooseHotspot = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.round(((event.clientX - rect.left) / rect.width) * 1000) / 10;
    const y = Math.round(((event.clientY - rect.top) / rect.height) * 1000) / 10;
    setF({ ...f, hotspot: { ...f.hotspot, x, y } });
  };
  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("اختر ملف صورة");
    try {
      setUploading(1);
      const data = await uploadFile(file, setUploading);
      setF((current) => ({ ...current, config: { ...current.config, image_url: data.url } }));
      toast.success("تم رفع صورة السؤال");
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر رفع الصورة"); }
    finally { setUploading(0); }
  };

  const save = async () => {
    if (!f.text.trim()) return toast.error("نص السؤال مطلوب");
    if (isChoice && f.options.some((o) => !o.trim())) return toast.error("أكمل جميع الخيارات");
    if (isMatching && (f.config.lefts.length < 2 || [...f.config.lefts, ...f.config.rights].some((v) => !v.trim()))) return toast.error("أكمل زوجين على الأقل");
    if (isHotspot && !f.config.image_url) return toast.error("ارفع صورة السؤال وحدد موضع الخطأ");
    if (isFormula && !f.formula_answers.trim()) return toast.error("أضف صيغة صحيحة واحدة على الأقل");
    setSaving(true);
    try {
      const body = {
        type: f.type, text: f.text, options: isChoice ? f.options : [],
        correct: f.type === "ordering" ? f.options.map((_, i) => String(i))
          : isMatching ? f.config.lefts.map((_, i) => String(i))
            : isHotspot ? f.hotspot
              : isFormula ? f.formula_answers.split("\n").map((v) => v.trim()).filter(Boolean)
                : (isChoice ? f.correct : null),
        weight: Number(f.weight) || 1, explanation: f.explanation,
        model_answer: f.model_answer, rubric: f.rubric, min_score: Number(f.min_score) || 50,
        required_keywords: f.required_keywords.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
        config: isMatching ? { lefts: f.config.lefts, rights: f.config.rights }
          : isHotspot ? { image_url: f.config.image_url, image_alt: f.config.image_alt }
            : isFormula ? { starter: f.config.starter } : {},
      };
      if (isNew) await api.post(`/admin/challenges/${cid}/questions`, body);
      else await api.put(`/admin/challenges/${cid}/questions/${q.id}`, body);
      toast.success(isNew ? "تمت إضافة السؤال" : "تم حفظ السؤال");
      qc.invalidateQueries({ queryKey: ["adm-challenges"] });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "إضافة سؤال" : "تعديل سؤال"} onClose={onClose} wide testId="q-form">
      <div className="flex justify-end mb-3">
        <button onClick={() => setPreview(!preview)} data-testid="q-preview" className="v2-btn-ghost v2-btn-sm"><Eye size={14} /> {preview ? "عودة للتحرير" : "معاينة كما يراه الموظف"}</button>
      </div>

      {preview ? (
        <div className="v2-inset p-5" data-testid="q-preview-box">
          <Chip tone="muted">{Q_TYPE_AR[f.type]}</Chip>
          <div className="text-lg font-semibold my-3">{f.text || "نص السؤال..."}</div>
          {isChoice ? (
            <div className="space-y-2">
              {f.options.map((o, i) => (
                <div key={i} className="p-3 rounded-2xl text-sm font-semibold border border-white/10">
                  {f.type === "ordering" ? `${i + 1}. ` : ""}{o || `خيار ${i + 1}`}
                </div>
              ))}
            </div>
          ) : isMatching ? (
            <div className="space-y-2">
              {f.config.lefts.map((left, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center rounded-2xl border border-white/10 p-3">
                  <span>{left || `عنصر ${i + 1}`}</span><span className="text-[#e3c799]">←</span><span>{f.config.rights[i] || `المطابقة ${i + 1}`}</span>
                </div>
              ))}
            </div>
          ) : isHotspot ? (
            f.config.image_url ? <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <img src={fileUrl(f.config.image_url)} alt={f.config.image_alt} className="block w-full h-auto bg-black/30" />
              <span className="absolute rounded-xl border-2 border-[#e3c799] bg-[#bb9661]/20" style={{
                left: `${f.hotspot.x - f.hotspot.w / 2}%`, top: `${f.hotspot.y - f.hotspot.h / 2}%`,
                width: `${f.hotspot.w}%`, height: `${f.hotspot.h}%`,
              }} />
            </div> : <div className="v2-inset p-8 text-center text-[#bcc8cf]">ارفع صورة السؤال أولًا.</div>
          ) : isFormula ? (
            <div dir="ltr" className="rounded-2xl border border-white/10 bg-[#17252f] p-4 font-mono text-sm text-[#f6f8fa] min-h-28 whitespace-pre-wrap">
              {f.config.starter || "=IF(A2>0, \"صحيح\", \"خطأ\")"}
            </div>
          ) : <textarea rows={4} disabled placeholder="اكتب إجابتك بالتفصيل..." className="v2-input resize-none" />}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="v2-label">نوع السؤال</label>
              <select value={f.type} onChange={(e) => changeType(e.target.value)} data-testid="q-type" className="v2-input">
                {Object.entries(Q_TYPE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="v2-label">وزن السؤال (الدرجة النسبية)</label>
              <input type="number" min="0.5" step="0.5" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} data-testid="q-weight" className="v2-input" />
            </div>
          </div>
          <div>
            <label className="v2-label">نص السؤال</label>
            <textarea rows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="q-text" className="v2-input resize-none" />
          </div>

          {isChoice && (
            <div>
              <label className="v2-label">
                {f.type === "ordering" ? "العناصر (أدخلها بالترتيب الصحيح — ستُعرض للموظف عشوائيًا)"
                  : f.type === "multi" ? "الخيارات (حدد كل الإجابات الصحيحة)" : "الخيارات (حدد الإجابة الصحيحة)"}
              </label>
              <div className="space-y-2">
                {f.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {f.type === "mcq" || f.type === "truefalse" ? (
                      <input type="radio" checked={String(f.correct) === String(i)} onChange={() => setF({ ...f, correct: String(i) })}
                        data-testid={`q-correct-${i}`} className="w-4 h-4 shrink-0 accent-[#91cab8]" />
                    ) : f.type === "multi" ? (
                      <input type="checkbox" checked={(f.correct || []).includes(String(i))}
                        onChange={() => {
                          const arr = f.correct || [];
                          setF({ ...f, correct: arr.includes(String(i)) ? arr.filter((x) => x !== String(i)) : [...arr, String(i)] });
                        }} data-testid={`q-correct-${i}`} className="w-4 h-4 shrink-0 accent-[#91cab8]" />
                    ) : (
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-[#bb9661]/15 text-[#e3c799]">{i + 1}</span>
                    )}
                    <input value={o} onChange={(e) => setOpt(i, e.target.value)} data-testid={`q-opt-${i}`} className="v2-input" disabled={f.type === "truefalse"} />
                    {f.type !== "truefalse" && f.options.length > 2 && (
                      <button onClick={() => delOpt(i)} className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center bg-[#B45309]/20 text-[#F7A08A]"><Trash2 size={13} /></button>
                    )}
                  </div>
                ))}
              </div>
              {f.type !== "truefalse" && <button onClick={addOpt} data-testid="q-add-opt" className="v2-btn-ghost v2-btn-sm mt-2"><Plus size={13} /> إضافة خيار</button>}
            </div>
          )}

          {isMatching && (
            <div>
              <label className="v2-label">أزواج التوصيل الصحيحة</label>
              <p className="text-xs text-[#bcc8cf] mb-2">سيُخلط العمود الثاني للموظف، بينما تحفظ المنصة المطابقة الصحيحة تلقائيًا.</p>
              <div className="space-y-2">
                {f.config.lefts.map((left, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                    <input value={left} onChange={(e) => setPair("lefts", i, e.target.value)} className="v2-input" placeholder="الدالة أو المفهوم" />
                    <span className="text-[#e3c799]">يرتبط بـ</span>
                    <input value={f.config.rights[i]} onChange={(e) => setPair("rights", i, e.target.value)} className="v2-input" placeholder="الشرح أو النتيجة" />
                    <button type="button" onClick={() => delPair(i)} disabled={f.config.lefts.length <= 2} className="w-9 h-9 rounded-lg bg-[#B45309]/20 text-[#F7A08A] disabled:opacity-30"><Trash2 size={14} className="mx-auto" /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addPair} className="v2-btn-ghost v2-btn-sm mt-2"><Plus size={13} /> إضافة زوج</button>
            </div>
          )}

          {isHotspot && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="v2-btn-ghost v2-btn-sm cursor-pointer">
                  <UploadCloud size={15} /> {uploading ? `جارٍ الرفع ${uploading}%` : "رفع صورة Excel أو الكود"}
                  <input type="file" accept="image/*" className="hidden" disabled={Boolean(uploading)} onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
                <input value={f.config.image_alt} onChange={(e) => setF({ ...f, config: { ...f.config, image_alt: e.target.value } })} className="v2-input !w-auto flex-1" placeholder="وصف الصورة لسهولة الوصول" />
              </div>
              {f.config.image_url && (
                <>
                  <p className="text-xs text-[#bcc8cf] flex items-center gap-2"><MousePointerClick size={14} /> اضغط في مركز مكان الخطأ، ثم عدّل حجم منطقة القبول إن احتجت.</p>
                  <button type="button" onClick={chooseHotspot} className="relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-black/30 cursor-crosshair">
                    <img src={fileUrl(f.config.image_url)} alt={f.config.image_alt} className="block w-full h-auto" />
                    <span className="absolute rounded-xl border-2 border-[#e3c799] bg-[#bb9661]/20 pointer-events-none" style={{
                      left: `${f.hotspot.x - f.hotspot.w / 2}%`, top: `${f.hotspot.y - f.hotspot.h / 2}%`,
                      width: `${f.hotspot.w}%`, height: `${f.hotspot.h}%`,
                    }} />
                  </button>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="v2-label">عرض منطقة القبول %</label><input type="number" min="4" max="60" value={f.hotspot.w} onChange={(e) => setF({ ...f, hotspot: { ...f.hotspot, w: Number(e.target.value) } })} className="v2-input" /></div>
                    <div><label className="v2-label">ارتفاع منطقة القبول %</label><input type="number" min="4" max="60" value={f.hotspot.h} onChange={(e) => setF({ ...f, hotspot: { ...f.hotspot, h: Number(e.target.value) } })} className="v2-input" /></div>
                  </div>
                </>
              )}
            </div>
          )}

          {isFormula && (
            <div className="space-y-3">
              <div>
                <label className="v2-label flex items-center gap-2"><Code2 size={14} /> الصيغة أو الكود الذي سيصححه الموظف</label>
                <textarea dir="ltr" rows={4} value={f.config.starter} onChange={(e) => setF({ ...f, config: { ...f.config, starter: e.target.value } })} className="v2-input resize-y font-mono text-left" placeholder={'=IF(A2>10,"ممتاز" "يحتاج مراجعة")'} />
              </div>
              <div>
                <label className="v2-label">الإجابات الصحيحة المقبولة (إجابة في كل سطر)</label>
                <textarea dir="ltr" rows={3} value={f.formula_answers} onChange={(e) => setF({ ...f, formula_answers: e.target.value })} className="v2-input resize-y font-mono text-left" placeholder={'=IF(A2>10,"ممتاز","يحتاج مراجعة")'} />
              </div>
            </div>
          )}

          {isText && (
            <>
              <div>
                <label className="v2-label">نموذج الإجابة (يستخدمه الذكاء الاصطناعي في التقييم)</label>
                <textarea rows={2} value={f.model_answer} onChange={(e) => setF({ ...f, model_answer: e.target.value })} data-testid="q-model" className="v2-input resize-none" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="v2-label">معايير تقييم AI (Rubric)</label>
                  <textarea rows={2} value={f.rubric} onChange={(e) => setF({ ...f, rubric: e.target.value })} data-testid="q-rubric" className="v2-input resize-none" />
                </div>
                <div>
                  <label className="v2-label">المفاهيم الأساسية (افصل بفاصلة)</label>
                  <textarea rows={2} value={f.required_keywords} onChange={(e) => setF({ ...f, required_keywords: e.target.value })} data-testid="q-keywords" className="v2-input resize-none" />
                </div>
                <div>
                  <label className="v2-label">الحد الأدنى للنجاح في السؤال %</label>
                  <input type="number" value={f.min_score} onChange={(e) => setF({ ...f, min_score: e.target.value })} data-testid="q-minscore" className="v2-input" />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="v2-label">شرح الإجابة (يظهر للموظف عند تفعيل «عرض الإجابات الصحيحة»)</label>
            <input value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} data-testid="q-explanation" className="v2-input" />
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving} data-testid="q-save" className="v2-btn w-full mt-5">{saving ? "جارٍ الحفظ..." : "حفظ السؤال"}</button>
    </Modal>
  );
}

export default function Quizzes() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });
  const [cid, setCid] = useState("");
  const [qForm, setQForm] = useState(null);
  const [delQ, setDelQ] = useState(null);
  const [settings, setSettings] = useState(null);
  const ch = rows.find((c) => c.id === (cid || rows[0]?.id));

  const s = settings || (ch ? {
    pass_score: ch.pass_score ?? 70, max_attempts: ch.max_attempts ?? 0,
    time_limit_minutes: ch.time_limit_minutes ?? 0, show_correct: !!ch.show_correct,
    show_score: ch.show_score !== false, retry_allowed: ch.retry_allowed !== false,
    quiz_enabled: ch.quiz_enabled !== false,
  } : null);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["adm-challenges"] }); qc.invalidateQueries({ queryKey: ["challenges"] }); };

  const saveSettings = async () => {
    try {
      await api.patch(`/admin/challenges/${ch.id}/quiz`, {
        ...s, pass_score: Number(s.pass_score), max_attempts: Number(s.max_attempts),
        time_limit_minutes: Number(s.time_limit_minutes),
      });
      toast.success("تم حفظ إعدادات الاختبار");
      setSettings(null);
      invalidate();
    } catch { toast.error("تعذّر الحفظ"); }
  };

  const removeQ = async () => {
    try { await api.delete(`/admin/challenges/${ch.id}/questions/${delQ}`); toast.success("تم حذف السؤال"); invalidate(); }
    catch { toast.error("تعذّر الحذف"); }
    setDelQ(null);
  };

  const dupQ = async (qid) => {
    try { await api.post(`/admin/challenges/${ch.id}/questions/${qid}/duplicate`); toast.success("تم نسخ السؤال"); invalidate(); }
    catch { toast.error("تعذّر النسخ"); }
  };

  const moveQ = async (idx, dir) => {
    const t = idx + dir;
    const qs = ch.questions || [];
    if (t < 0 || t >= qs.length) return;
    const order = qs.map((q) => q.id);
    [order[idx], order[t]] = [order[t], order[idx]];
    try { await api.post(`/admin/challenges/${ch.id}/questions/reorder`, { order }); invalidate(); }
    catch { toast.error("تعذّر إعادة الترتيب"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-quizzes">
      <PageHead icon={FileQuestion} title="الاختبارات والأسئلة" sub="محرر تفاعلي بعشرة أنواع، يشمل التوصيل واكتشاف الخطأ في الصورة وتصحيح صيغ Excel." />

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <>
          <select value={cid || rows[0]?.id || ""} onChange={(e) => { setCid(e.target.value); setSettings(null); }} data-testid="quiz-ch-select" className="v2-input !w-auto text-sm font-bold">
            {rows.map((c) => <option key={c.id} value={c.id}>{`اليوم ${c.day} — ${c.title}`}</option>)}
          </select>

          {ch && s && (
            <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
              <GlassCard className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold">أسئلة الاختبار ({ch.questions?.length || 0})</h3>
                  <button onClick={() => setQForm({})} data-testid="q-add" className="v2-btn v2-btn-sm"><Plus size={14} /> إضافة سؤال</button>
                </div>
                <div className="space-y-3">
                  {(ch.questions || []).map((q, i) => (
                    <div key={q.id} className="v2-inset p-4" data-testid={`q-item-${i}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1">
                          <button onClick={() => moveQ(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowUp size={12} /></button>
                          <button onClick={() => moveQ(i, 1)} disabled={i === (ch.questions.length - 1)} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowDown size={12} /></button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Chip tone="emerald">{Q_TYPE_AR[q.type] || q.type}</Chip>
                            <Chip tone="muted">وزن {q.weight || 1}</Chip>
                          </div>
                          <div className="text-sm font-semibold leading-relaxed">{q.text}</div>
                          {q.options?.length > 0 && (
                            <div className="text-[11px] text-[#bcc8cf] mt-1 truncate">
                              {q.options.map((o, oi) => {
                                const isCorrect = Array.isArray(q.correct) ? q.correct.includes(String(oi)) : String(q.correct) === String(oi);
                                return <span key={oi} className={isCorrect && q.type !== "ordering" ? "font-bold text-[#91cab8]" : ""}>{o}{oi < q.options.length - 1 ? " • " : ""}</span>;
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => setQForm(q)} data-testid={`q-edit-${i}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center hover:opacity-75"><Pencil size={13} /></button>
                          <button onClick={() => dupQ(q.id)} data-testid={`q-dup-${i}`} className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center hover:opacity-75"><Copy size={13} /></button>
                          <button onClick={() => setDelQ(q.id)} data-testid={`q-del-${i}`} className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#B45309]/20 text-[#F7A08A]"><Trash2 size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(ch.questions || []).length === 0 && <p className="text-sm text-[#bcc8cf] text-center py-6">لا توجد أسئلة بعد — أضف أول سؤال.</p>}
                </div>
              </GlassCard>

              <GlassCard className="p-5 space-y-3">
                <h3 className="font-bold mb-1">إعدادات الاختبار</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="v2-label">درجة النجاح %</label><input type="number" value={s.pass_score} onChange={(e) => setSettings({ ...s, pass_score: e.target.value })} data-testid="quiz-pass" className="v2-input" /></div>
                  <div><label className="v2-label">عدد المحاولات (0=غير محدود)</label><input type="number" value={s.max_attempts} onChange={(e) => setSettings({ ...s, max_attempts: e.target.value })} data-testid="quiz-attempts" className="v2-input" /></div>
                </div>
                <div><label className="v2-label">مدة الاختبار بالدقائق (0=بدون وقت)</label><input type="number" value={s.time_limit_minutes} onChange={(e) => setSettings({ ...s, time_limit_minutes: e.target.value })} data-testid="quiz-time" className="v2-input" /></div>
                <Toggle checked={s.quiz_enabled} onChange={(v) => setSettings({ ...s, quiz_enabled: v })} label="الاختبار مفعّل" testId="quiz-enabled" />
                <Toggle checked={s.retry_allowed} onChange={(v) => setSettings({ ...s, retry_allowed: v })} label="السماح بإعادة المحاولة" testId="quiz-retry" />
                <Toggle checked={s.show_score} onChange={(v) => setSettings({ ...s, show_score: v })} label="عرض الدرجة مباشرة للموظف" testId="quiz-showscore" />
                <Toggle checked={s.show_correct} onChange={(v) => setSettings({ ...s, show_correct: v })} label="عرض الإجابات الصحيحة بعد الاختبار" testId="quiz-showcorrect" />
                <p className="text-[11px] text-[#bcc8cf]">نسبة مشاهدة الفيديو المطلوبة قبل فتح الاختبار تُضبط من صفحة «الفيديوهات» ({ch.video_required_pct ?? 80}%).</p>
                <button onClick={saveSettings} data-testid="quiz-save-settings" className="v2-btn w-full !h-11"><Save size={15} /> حفظ الإعدادات</button>
                <div className="pt-2">
                  <div className="flex justify-between text-[11px] text-[#bcc8cf] mb-1"><span>درجة النجاح</span><span>{s.pass_score}%</span></div>
                  <Bar value={Number(s.pass_score)} />
                </div>
              </GlassCard>
            </div>
          )}
        </>
      )}

      {qForm !== null && ch && <QuestionForm cid={ch.id} q={qForm.id ? qForm : null} onClose={() => setQForm(null)} />}
      {delQ && <Confirm title="حذف السؤال" danger confirmLabel="نعم، احذف"
        body="سيتم حذف السؤال نهائيًا من الاختبار وتسجيل العملية في سجل الإدارة. هل أنت متأكد؟"
        onConfirm={removeQ} onClose={() => setDelQ(null)} testId="q-del-confirm" />}
    </div>
  );
}
